import { z } from 'zod';
import {
  assemblyDaySignatureResponseSchema,
  assemblyDayWorkerInputSchema,
} from '../assembly/assembly-day-log.schema';

/**
 * Bau-Tagesbericht, wie ein **Montage-Partner** ihn im Portal führt
 * (ADR 0074 + ADR 0087).
 *
 * Kein zweites Formular: Der Fremdmonteur füllt denselben `AssemblyDayLog` wie
 * unsere eigenen Monteure und lässt den Bauherrn auf seinem Gerät
 * unterschreiben. Alles andere hätte zwei Wahrheiten über denselben Bautag
 * erzeugt — und der Tagesbericht ist ein **Beweisdokument**: Behinderungen und
 * Regieleistungen sind die Grundlage für Nachträge, die Gegenzeichnung macht
 * sie belastbar.
 *
 * Verengt gegenüber der internen Antwort um genau eine Sache: `employeeId` an
 * den Personalzeilen. Die HR-Id unserer Mitarbeiter ist ein interner Schlüssel;
 * der Name steht ohnehin daneben.
 */
export const portalDayWorkerSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  role: z.string(),
  hours: z.number().nullable(),
  position: z.number().int(),
});
export type PortalDayWorker = z.infer<typeof portalDayWorkerSchema>;

/**
 * Wer an diesem Tag montiert hat — vom Partner erfasst. Bis auf `employeeId`
 * dasselbe Formular wie intern: Der Partner kann keine Zeile an einen unserer
 * HR-Datensätze hängen, und raten soll er es erst recht nicht. Seine Leute
 * stehen namentlich da, das ist der ganze Zweck des Blocks.
 */
export const portalDayWorkerInputSchema = assemblyDayWorkerInputSchema.omit({ employeeId: true });
export type PortalDayWorkerInput = z.infer<typeof portalDayWorkerInputSchema>;

/** Ändern: alle Felder optional (Auto-Save je Feld, wie am Tag selbst). */
export const updatePortalDayWorkerSchema = portalDayWorkerInputSchema.partial();
export type UpdatePortalDayWorkerInput = z.infer<typeof updatePortalDayWorkerSchema>;

export const portalDayLogSchema = z.object({
  id: z.string().uuid(),
  /** Laufende Nummer des Tagesberichts innerhalb des Einsatzes. */
  number: z.number().int().nullable(),
  logDate: z.string().datetime(),
  weatherConditions: z.array(z.string()),
  temperatureC: z.number().nullable(),
  workFrom: z.string().nullable(),
  workTo: z.string().nullable(),
  workHours: z.number().nullable(),
  travelHours: z.number().nullable(),
  workerCount: z.number().int().nullable(),
  work: z.string().nullable(),
  obstructions: z.string().nullable(),
  specialEvents: z.string().nullable(),
  extraWork: z.string().nullable(),
  workers: z.array(portalDayWorkerSchema),
  signatures: z.array(assemblyDaySignatureResponseSchema),
  /**
   * Gesetzt ⇒ unterschrieben und damit **unveränderlich**. Was der Bauherr
   * gegengezeichnet hat, darf sich nachträglich nicht mehr ändern — sonst wäre
   * die Unterschrift nichts wert.
   */
  lockedAt: z.string().datetime().nullable(),
});
export type PortalDayLog = z.infer<typeof portalDayLogSchema>;

export const portalDayLogsResponseSchema = z.object({
  /** Titel des Einsatzes — Kopfzeile der Seite. */
  taskTitle: z.string(),
  /**
   * `false`, sobald der Gesamtbericht bei uns fertiggestellt ist. Dann ist der
   * Einsatz aus unserer Sicht abgeschlossen und der Partner kann nichts mehr
   * nachtragen — er soll das sehen, statt in einen Fehler zu laufen.
   */
  isEditable: z.boolean(),
  days: z.array(portalDayLogSchema),
});
export type PortalDayLogsResponse = z.infer<typeof portalDayLogsResponseSchema>;
